// Where the board is, as one value (2026-10-19): the game ply, the reader's
// line and the look back, with the invariants between them, and every
// command on it as one pure transition. The session holds a `Cursor` and
// delegates each of its methods to `reduce`, publishing `stateFieldsOf` it;
// before, the three fields were kept apart and each method changed them its
// own way, and a case slipped: a link followed from a line changed the
// address and left the board at the line's end. `cursor.test.ts` is the
// table of every command from every kind of position, and a random walk
// that checks the invariants after each.
//
// The invariants: the ply is within the game; a line branches at the ply
// (`line.fromPly === ply`) from the game's position there; a look back is
// below the head (the line's end, or the ply); the board always has a
// position. A line taken all the way back is kept, empty, so the same move
// re-enters it: it is not published (`exploring` is undefined), and any step
// along the game drops it.
import type { Fen, Move } from '@chess/api-model';
import { Line } from './line.ts';
import type { ReviewExploration } from './index.ts';

/** What the cursor needs of the game: its moves and the position before each, and after the last. */
export interface CursorGame {
  moves: readonly Move[];
  positions: readonly Fen[];
}

export interface Cursor {
  /** 0 is the starting position; `moves.length` after the last move. While a line is open, the ply it branches from. */
  ply: number;
  /** The reader's line, possibly empty (taken all the way back and kept). */
  line?: Line;
  /** A ply along the path looked back at, below the head. */
  browse?: number;
}

export type Command =
  | { type: 'go'; ply: number }
  | { type: 'back' }
  | { type: 'forward' }
  | { type: 'start' }
  | { type: 'end' }
  /** A legal move from the board's position, already resolved by the rules: the move and the position after it. */
  | { type: 'play'; move: Move; fen: Fen }
  | { type: 'return' }
  /** Look back at a ply along the path; none, or the head, shows the head again. `range` holds the look within it (a puzzle's). */
  | { type: 'browse'; ply?: number; range?: { from: number; to: number } };

function clamp(ply: number, moveCount: number): number {
  if (!Number.isFinite(ply)) return 0;
  return Math.min(Math.max(Math.trunc(ply), 0), moveCount);
}

const onLine = (c: Cursor): boolean => !!c.line && !c.line.empty;

/** The far end of the path the board has travelled: the line's end, or the ply. */
export function headPlyOf(c: Cursor): number {
  return onLine(c) ? c.line!.headPly : c.ply;
}

/** The ply along the path the board shows: the one looked back at, or the head. */
export function viewPlyOf(c: Cursor): number {
  return c.browse ?? headPlyOf(c);
}

/** The position at a ply along the path: the line's past the branch, the game's otherwise. */
export function pathPositionOf(c: Cursor, game: CursorGame, ply: number): Fen | undefined {
  if (onLine(c) && ply > c.line!.fromPly) return c.line!.positionAt(ply);
  return game.positions[ply];
}

/** The position on the board. */
export function positionOf(c: Cursor, game: CursorGame): Fen | undefined {
  return pathPositionOf(c, game, viewPlyOf(c));
}

/** The three fields of the review's state the cursor stands behind. */
export function stateFieldsOf(c: Cursor): { ply: number; exploring: ReviewExploration | undefined; browse: number | undefined } {
  return { ply: c.ply, exploring: onLine(c) ? c.line!.toExploration() : undefined, browse: c.browse };
}

/** Whether two cursors publish the same thing (the kept empty line counts: dropping it is a change). */
export function sameCursor(a: Cursor, b: Cursor): boolean {
  return a.ply === b.ply && a.line === b.line && a.browse === b.browse;
}

/**
 * The cursor after a command; `undefined` for a move refused (only while
 * looking back, so only `play` may give it). A command that changes nothing
 * returns the cursor itself.
 */
export function reduce(c: Cursor, command: Extract<Command, { type: 'play' }>, game: CursorGame): Cursor | undefined;
export function reduce(c: Cursor, command: Exclude<Command, { type: 'play' }>, game: CursorGame): Cursor;
export function reduce(c: Cursor, command: Command, game: CursorGame): Cursor | undefined;
export function reduce(c: Cursor, command: Command, game: CursorGame): Cursor | undefined {
  const last = game.moves.length;
  const looking = c.browse !== undefined;
  switch (command.type) {
    case 'go':
      return { ply: clamp(command.ply, last) };
    case 'back':
      if (looking) return c;
      if (onLine(c)) return { ...c, line: c.line!.back() };
      return { ply: clamp(c.ply - 1, last) };
    case 'forward':
      if (looking || onLine(c)) return c;
      return { ply: clamp(c.ply + 1, last) };
    case 'start':
      return looking ? c : { ply: 0 };
    case 'end':
      return looking || onLine(c) ? c : { ply: last };
    case 'play': {
      if (looking) return undefined;
      // The game's own next move is a step, not a deviation.
      if (!onLine(c) && game.moves[c.ply]?.uci === command.move.uci) return { ply: clamp(c.ply + 1, last) };
      const base = c.line && c.line.fromPly === c.ply ? c.line : new Line(c.ply, game.positions[c.ply]);
      return { ply: c.ply, line: base.play(command.move, command.fen) };
    }
    case 'return':
      return onLine(c) ? { ply: c.line!.fromPly } : c;
    case 'browse': {
      const head = headPlyOf(c);
      let next = command.ply === undefined || !Number.isFinite(command.ply) ? undefined : Math.trunc(command.ply);
      if (command.range && next !== undefined) next = Math.min(Math.max(next, command.range.from), command.range.to);
      const view = next === undefined || next >= head ? undefined : Math.max(next, 0);
      return view === c.browse ? c : { ...c, browse: view };
    }
  }
}

/** What is wrong with a cursor for a game; nothing when it is sound. */
export function invariantsOf(c: Cursor, game: CursorGame): string[] {
  const out: string[] = [];
  const last = game.moves.length;
  if (!Number.isInteger(c.ply) || c.ply < 0 || c.ply > last) out.push(`ply ${c.ply} outside the game of ${last} moves`);
  if (c.line) {
    if (c.line.fromPly !== c.ply) out.push(`a line branching at ${c.line.fromPly} while the ply is ${c.ply}`);
    if (c.line.start !== game.positions[c.line.fromPly]) out.push(`a line whose start is not the game's position at ${c.line.fromPly}`);
  }
  if (c.browse !== undefined) {
    const head = headPlyOf(c);
    if (!Number.isInteger(c.browse) || c.browse < 0 || c.browse >= head) out.push(`a look back at ${c.browse} with the head at ${head}`);
  }
  if (out.length === 0 && positionOf(c, game) === undefined) out.push('no position on the board');
  return out;
}
